import {
  RowDragEndEvent,
  RowDragMoveEvent,
  RowDropZoneParams,
} from '@ag-grid-community/core';
import { useCallback, useEffect, useRef } from 'react';
import { LocalAttachment } from 'stream-chat';
import { AttachmentManager } from 'stream-chat/dist/types/messageComposer/attachmentManager';

import { FILE_TYPE } from 'src/constants/fileConsts';
import type { FileAttachment } from 'src/features/channel/custom-attachments/attachmentUtils';
import { useContextBar } from 'src/hooks/useContextBar';
import { FilesTableData } from 'src/store/files/types';

interface UseMessageInputDropZoneProps {
  isInternalChat: boolean;
  attachmentManager: AttachmentManager;
  setFileDropHintColor: (fileDropHintColor: FileDropHintColor | null) => void;
}

export enum FileDropHintColor {
  VALID = 'bg-blue-100',
  INVALID = 'bg-red-100',
}

export const useMessageInputDropZone = ({
  isInternalChat,
  attachmentManager,
  setFileDropHintColor,
}: UseMessageInputDropZoneProps) => {
  const { gridApi } = useContextBar();
  const messageInputRef = useRef<HTMLDivElement>(null);

  // No links or folders
  const isDroppable = useCallback((fileData: FilesTableData) => {
    const value = fileData.type === FILE_TYPE.FILE && fileData.fileUrl === '';
    return value;
  }, []);

  // Handle file drop from AgGrid
  const handleFileDrop = useCallback(
    (fileData: FilesTableData) => {
      if (!isDroppable(fileData)) {
        return;
      }
      // Create a properly typed file attachment
      const fileAttachment: FileAttachment & LocalAttachment = {
        type: 'file_id',
        fields: [
          {
            title: 'file_id',
            value: fileData.fileId,
          },
          {
            title: 'file_key',
            value: fileData.fileKey,
          },
          {
            title: 'identity_id',
            value: fileData.identityId,
          },
        ],
        title: fileData.fileName,
        // Fake local metadata because it's required by the API
        localMetadata: {
          id: fileData.fileId,
          uploadState: 'finished' as const,
          file: new File([], fileData.fileName),
        },
      };

      // Add file as custom attachment using attachmentManager
      attachmentManager.upsertAttachments([fileAttachment]);
    },
    [attachmentManager, isDroppable],
  );

  const setColorHint = useCallback(
    (fileData: FilesTableData | null) => {
      if (!fileData) {
        setFileDropHintColor(null);
      } else if (isDroppable(fileData)) {
        setFileDropHintColor(FileDropHintColor.VALID);
      } else {
        setFileDropHintColor(FileDropHintColor.INVALID);
      }
    },
    [isDroppable, setFileDropHintColor],
  );

  // Set up drop zone on message input when in internal chat
  useEffect(() => {
    if (!isInternalChat || !gridApi || !messageInputRef.current) {
      return;
    }

    const dropZoneParams: RowDropZoneParams = {
      getContainer() {
        return messageInputRef.current!;
      },
      onDragStop(params: RowDragEndEvent) {
        const fileData = params.node.data as FilesTableData;
        setColorHint(null);
        handleFileDrop(fileData);
      },
      onDragEnter(params: RowDragMoveEvent) {
        setColorHint(params.node.data as FilesTableData);
      },
      onDragLeave() {
        setColorHint(null);
      },
    };

    gridApi.removeRowDropZone(dropZoneParams);
    gridApi.addRowDropZone(dropZoneParams);

    return () => {
      // Clean up drop zone when component unmounts or dependencies change
      gridApi.removeRowDropZone(dropZoneParams);
    };
  }, [isInternalChat, gridApi, handleFileDrop, setColorHint]);

  return {
    messageInputRef,
  };
};
